/**
 * Module dependencies.
 */

import { CardContent } from '@mui/material';
import {
  CardDescription,
  CardImage,
  Container,
  Section,
  ServiceCard,
  ServicesGrid,
  Title,
  TitleWrapper,
} from './services.style';

import { Description } from 'components/atoms/description/description';
import { FadeInUp } from 'components/atoms/in-view-once/fade-in-up';
import { InViewOnce } from 'components/atoms/in-view-once/in-view-once';
import { JSX, useMemo } from 'react';
import { ServiceType } from 'types/service';
import { SubTitle } from 'components/atoms/sub-title/sub-title';
import { getAsset } from 'utils/resolve-admin';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  services: ServiceType[];
};

/**
 * Export `Services` component.
 */

export function Services({ services }: Props): JSX.Element {
  const [translate, i18n] = useTranslation();
  const data = useMemo(() => {
    return (services ?? []).map((item) => {
      const lang = i18n.language;

      return {
        description:
          item.description[lang] ??
          item.description[i18n.options.fallbackLng as string],
        id: item.id,
        src: getAsset(item.image?.filename_disk),
        title:
          item.title[lang] ?? item.title[i18n.options.fallbackLng as string],
      };
    });
  }, [i18n.language, i18n.options.fallbackLng, services]);

  return (
    <Section id={'servicos'}>
      <Container>
        <TitleWrapper>
          <InViewOnce AnimationComponent={FadeInUp}>
            <Title>{translate('home.services.title')}</Title>
          </InViewOnce>

          <InViewOnce AnimationComponent={FadeInUp}>
            <Description>{translate('home.services.description')}</Description>
          </InViewOnce>
        </TitleWrapper>

        <ServicesGrid>
          {data.map((service) => (
            <InViewOnce AnimationComponent={FadeInUp} key={service.id}>
              <ServiceCard>
                <CardImage image={service.src} title={service.title} />

                <CardContent>
                  <SubTitle>{service.title}</SubTitle>

                  <CardDescription>{service.description}</CardDescription>
                </CardContent>
              </ServiceCard>
            </InViewOnce>
          ))}
        </ServicesGrid>
      </Container>
    </Section>
  );
}
